import { ExportRow, ExportSheet } from './exportExcel';

/**
 * Datos de cabecera del reporte, ya resueltos a texto por quien exporta
 * (los nombres de proyecto y de controlador se resuelven contra otras listas).
 */
export interface DailyReportExportHeader {
  dateLabel: string;
  statusLabel: string;
  projectName: string;
  controllerName: string;
  totalHh?: number;
  totalHm?: number;
  observations?: string;
}

/**
 * Hojas de un reporte diario: resumen + personal, equipos, producción y
 * subcontratos de cada frente de trabajo.
 *
 * `detailList` es la respuesta de `/Daily-reports/{id}/details`. Se accede de forma
 * defensiva porque el tipo declarado (DailyReportDetails) no coincide con el payload
 * real: las entradas traen `workFrontDetails`, no `workFronts`, y el nombre de la
 * actividad llega en `activityName` o en la actividad maestra anidada.
 */
export const buildDailyReportSheets = (
  header: DailyReportExportHeader,
  detailList: any[],
): ExportSheet[] => {
  const resumen: ExportRow[] = [
    { 'Campo': 'Fecha Reporte', 'Valor': header.dateLabel },
    { 'Campo': 'Estado', 'Valor': header.statusLabel },
    { 'Campo': 'Proyecto', 'Valor': header.projectName },
    { 'Campo': 'Control de Obras', 'Valor': header.controllerName },
    { 'Campo': 'Total HH', 'Valor': header.totalHh ?? '' },
    { 'Campo': 'Total HM', 'Valor': header.totalHm ?? '' },
    { 'Campo': 'Observaciones', 'Valor': header.observations || '' },
  ];

  const personal: ExportRow[] = [];
  const equipos: ExportRow[] = [];
  const produccion: ExportRow[] = [];
  const subcontratos: ExportRow[] = [];

  (detailList || []).forEach((detail: any) => {
    const activityName = detail?.activity?.activityName || detail?.activity?.activity?.name || 'Sin actividad';

    (detail?.workFrontDetails || []).forEach((front: any) => {
      const frente = front?.workFront?.frontNumber ?? front?.frontNumber ?? '';

      (front?.personnel || []).forEach((row: any) => personal.push({
        'Actividad': activityName,
        'Frente': frente,
        'RUT': row?.employee?.rut || '',
        'Nombre': [row?.employee?.name, row?.employee?.firstName, row?.employee?.lastName].filter(Boolean).join(' '),
        'Cargo': row?.employee?.position || '',
      }));

      (front?.equipment || []).forEach((row: any) => equipos.push({
        'Actividad': activityName,
        'Frente': frente,
        'Patente': row?.equipment?.vehicleNumber || '',
        'Equipo': row?.equipment?.name || '',
        'Propiedad': row?.equipment?.owner ? 'Propio' : 'Arrendado',
        'Chofer': row?.equipment?.employee?.name || '',
      }));

      (front?.production || []).forEach((row: any) => produccion.push({
        'Actividad': activityName,
        'Frente': frente,
        'Torre': row?.tower?.towerNumber || '',
        'Avance del día (%)': row?.progressPercentage ?? '',
        'Avance del día': row?.advance ?? '',
        'Acumulado (%)': row?.cumulativePercentage ?? '',
        'Acumulado': row?.cumulativeAdvance ?? '',
        'V.N.T. (m3)': row?.naturalGroundVolumeVnt ?? '',
        'Volumen Afloramiento (m3)': row?.outcropVolume ?? '',
        'Avance Real (m)': row?.realAdvance ?? '',
        'Motivo (Avance 0)': row?.zeroProgressReason || '',
      }));

      (front?.subcontracts || []).forEach((row: any) => subcontratos.push({
        'Actividad': activityName,
        'Frente': frente,
        'Empresa': row?.subcontractor?.businessName || '',
        'RUT Empresa': row?.subcontractor?.rut || '',
        'Recurso': row?.resourceType === 'PERSONNEL' ? 'Personal' : 'Equipos',
        'Dotación': row?.headcount ?? '',
        'Horas Totales': row?.totalHours ?? '',
      }));
    });
  });

  return [
    { name: 'Resumen', rows: resumen },
    { name: 'Personal', rows: personal },
    { name: 'Equipos', rows: equipos },
    { name: 'Producción', rows: produccion },
    { name: 'Subcontratos', rows: subcontratos },
  ];
};

/** true si el reporte no trae ninguna fila de detalle (solo quedaría el resumen). */
export const hasNoDetailRows = (sheets: ExportSheet[]): boolean =>
  sheets.filter(sheet => sheet.name !== 'Resumen').every(sheet => sheet.rows.length === 0);
